import { Injectable } from '@angular/core';
import { firstValueFrom, retry, Subject, Observable } from 'rxjs';
import { CallService } from 'src/app/services/learner/call.service';
import { correctTranscript } from 'src/app/utils/transcript-corrector';
import { correctedTranscript } from 'src/app/utils/transcript-corrector';
import { AppConstants } from 'src/app/app-constants';
import { CallStoreService } from '../call-store/call-store.service';
import { ControlService } from '../control.service';
import { DeviceSettingsService } from '../device-settings.service';
import { CallValidationService } from '../call-validation/call-validation.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { SplitIoService } from 'src/app/services/external-tools/split-io.service';
import { ISttConnectionConfig } from 'src/app/interfaces/call/istt-connection-config';
import { TranslocoService } from '@jsverse/transloco';
@Injectable({
  providedIn: 'root',
})
export class AssemblyService {
  isPauseOnProcess: boolean = false;
  isResumeOnProcess: boolean = false;
  isErrorOnAudioProcessor: boolean = false;
  isStopAudioAPIOnProcess: boolean = false;
  isPrewarmOnProcess: boolean = false;
  isStartRecordingOnProcess: boolean = false;

  mediaStream: MediaStream | null = null;
  audioContext: AudioContext | null = null;
  audioWorkletNode: AudioWorkletNode | null = null;
  mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  audioBufferQueue: Int16Array = new Int16Array(0);
  assemblyAIToken: string | null = null;
  sttConfig: ISttConnectionConfig | null = null;
  websocket: WebSocket | null = null;

  isInternetConnected: boolean = true;
  partial: string = '';
  final: string = '';
  lastPartialUsed: string = '';
  shouldTranscriberRun: boolean = true;
  isStopRecordingInProgress: boolean = false;
  assemblyAwaitFinalEnabled: boolean = false;
  maxTurnSilenceDelay: number = 0;
  private isU3ProModel: boolean = false;
  private isU35ProModel: boolean = false;
  private pendingFinalResolver: ((transcript: string) => void) | null = null;

  // How long awaitFinalTranscript() waits for end_of_turn=true after ForceEndpoint.
  // U3.5 Pro delivers the turn's final 0.3–1s after its last partial, so 1s has no margin.
  private readonly AWAIT_FINAL_TIMEOUT_MS = 1000;
  private readonly AWAIT_FINAL_TIMEOUT_U35_PRO_MS = 1500;
  // With no open partial an already-closed turn sends no reply to ForceEndpoint,
  // so only wait briefly for speech that may still be in flight.
  private readonly AWAIT_FINAL_SKIP_TIMEOUT_MS = 500;

  // turn_order tracking (AssemblyAI v3 numbers every turn, restarting at 0 per session).
  // lastTurnOrder/lastTurnClosed describe the newest turn seen; awaitedTurnOrder is the
  // turn whose final awaitFinalTranscript() is waiting for; sentTurnOrder is the turn
  // that was still open when the answer was sent — its late final belongs to that
  // answer and must not leak into the next one.
  private lastTurnOrder: number | null = null;
  private lastTurnClosed = true;
  private awaitedTurnOrder: number | null = null;
  private sentTurnOrder: number | null = null;

  get failsafeDelay(): number {
    return this.maxTurnSilenceDelay + 1000;
  }

  // U3.5 Pro sends partials only every 1.5–2s, so the fixed-timer path would
  // routinely cut answers short — await-final is enforced for it regardless of
  // the Assembly_Await_Final treatment. Other models follow the flag.
  get isAwaitFinalActive(): boolean {
    return this.assemblyAwaitFinalEnabled || this.isU35ProModel;
  }

  private get awaitFinalTimeoutMs(): number {
    return this.isU35ProModel
      ? this.AWAIT_FINAL_TIMEOUT_U35_PRO_MS
      : this.AWAIT_FINAL_TIMEOUT_MS;
  }

  private readonly _speechStarted$ = new Subject<void>();
  readonly onSpeechStarted$: Observable<void> =
    this._speechStarted$.asObservable();

  private readonly _audioLevel$ = new Subject<number>();
  readonly onAudioLevel$: Observable<number> = this._audioLevel$.asObservable();

  // Leftover-turn suppression: audio the user spoke after pressing Continue (or
  // buffered server-side before the mic paused) is flushed by AssemblyAI as a
  // Turn once the mic resumes for the next question. That turn would otherwise
  // set a partial and enable Continue with no fresh speech. AssemblyAI's own
  // silence detection can also open that Turn's turn_order *before* Continue is
  // even pressed (rep pauses to think), so we can't rely on "first observed
  // inside the window" — we tag a turn_order as stale on ANY sighting while the
  // window is active, regardless of when it was first seen, and once tagged it
  // stays dropped for the rest of the session (covers the trailing final that
  // arrives after the window closes too). Turns never touched during the window
  // pass through untouched. Only active when isAwaitFinalActive.
  private discardStaleTurnWindowActive = false;
  private readonly staleTurnOrders = new Set<number>();

  private isResetting = false;
  private lastReconnectAt = 0;
  private keepAliveInterval: ReturnType<typeof setInterval> | null = null;
  private readonly RECONNECT_COOLDOWN_MS = 2_000;
  private readonly KEEPALIVE_INTERVAL_MS = 5_000;
  private _lastChunkLogAt = 0;

  // Named bound reference so the same function can be passed to removeEventListener
  private readonly onNetworkRestored = () => {
    if (
      !this.websocket &&
      !this.isResetting &&
      this.callStore.getIsAssemblyInitialized()
    ) {
      this.logger.log('[CALL] assembly - network restored, reconnecting');
      this.resetAndReconnect('network restored');
    }
  };

  constructor(
    private callService: CallService,
    private callStore: CallStoreService,
    private controlService: ControlService,
    private deviceSettingsService: DeviceSettingsService,
    private callValidationService: CallValidationService,
    private logger: LoggerService,
    private splitIoService: SplitIoService,
    private translocoService: TranslocoService,
  ) {}

  async init(): Promise<void> {
    this.callStore.setIsAssemblyInitialized(true);
    await this.stop();
    // Registered after stop() to avoid the remove inside stop() undoing it.
    // Remove first in case init() is called more than once to prevent duplicates.
    window.removeEventListener('online', this.onNetworkRestored);
    window.addEventListener('online', this.onNetworkRestored);
    this.sttConfig = await this.getSttConfig();
    this.assemblyAIToken = this.sttConfig.token;

    const callUuid = this.callStore.getCallUuid();
    this.splitIoService.setAttributes({ call_uuid: callUuid });

    this.applySttUrlSettings(this.sttConfig.url);

    const awaitFinalTreatment = await this.splitIoService.getTreatment(
      AppConstants.ASSEMBLY_AWAIT_FINAL,
    );

    this.assemblyAwaitFinalEnabled = awaitFinalTreatment === 'on';

    this.logger.log('[CALL] assembly - init', {
      isU3ProModel: this.isU3ProModel,
      isU35ProModel: this.isU35ProModel,
      assemblyAwaitFinalEnabled: this.assemblyAwaitFinalEnabled,
      isAwaitFinalActive: this.isAwaitFinalActive,
    });
  }

  private applySttUrlSettings(url: string): void {
    const configUrl = new URL(url);
    const speechModel = configUrl.searchParams.get('speech_model');
    this.isU3ProModel =
      speechModel === 'u3-rt-pro' ||
      speechModel === 'universal-3-5-pro' ||
      speechModel === 'universal-3-6-pro';
    this.isU35ProModel = speechModel === 'universal-3-5-pro';
    this.maxTurnSilenceDelay = Number(
      configUrl.searchParams.get('max_turn_silence') ?? 0,
    );
    // Assembly_Turn_Settings flag removed — force format_turns=false on the
    // connection itself, regardless of what the backend's STT config sends,
    // so it always matches the hard-coded turn_is_formatted=false filter in
    // handleTranscript. U3 Pro never uses this param, so it's left untouched
    // for that model. Written back onto sttConfig.url so startTranscriber()
    // picks up the corrected value without any other change.
    if (!this.isU3ProModel) {
      configUrl.searchParams.set('format_turns', 'false');
      if (this.sttConfig) {
        this.sttConfig.url = configUrl.toString();
      }
    }
  }

  // =========================
  // SIMPLIFIED RESTART
  // =========================
  async restartTranscriber(startRecording: boolean = true): Promise<void> {
    this.logger.log('[CALL] assembly - restart transcriber', {
      startRecording,
    });

    if (!this.callStore.getIsAssemblyInitialized()) {
      this.logger.log(
        '[CALL] assembly - not initialized, running init before restart',
      );
      await this.init();
      startRecording = true;
    }

    await this.resetAndReconnect('restart transcriber', startRecording);
  }

  // =========================
  // SIMPLIFIED START
  // =========================
  async startTranscriber(startRecording: boolean = true): Promise<void> {
    this.logger.log('[CALL] assembly - start transcriber', {
      startRecording,
    });

    if (!this.assemblyAIToken || !this.sttConfig) {
      await this.resetAndReconnect('missing stt config');
      return;
    }

    if (this.websocket) {
      await this.forceCleanup();
    }

    this.isStopRecordingInProgress = false;

    const wsUrl = `${this.sttConfig.url}&token=${this.assemblyAIToken}`;

    // Gated behind await-final — when inactive, keep exact pre-fix behavior.
    if (this.isAwaitFinalActive) {
      console.log(`AssemblyAI ::: Await final enabled, preserving stale partial before restart`);
      this.preserveStalePartialBeforeRestart();
    }
    else {
      console.log(`AssemblyAI ::: Await final disabled, clearing stale partial before restart`);
    }

    this.websocket = new WebSocket(wsUrl);

    this.setupWebSocketHandlers(startRecording);
  }

  // =========================
  // SIMPLIFIED SOCKET HANDLERS
  // =========================
  private setupWebSocketHandlers(startRecording: boolean): void {
    if (!this.websocket) return;

    // Capture the instance at setup time. Any handler that fires after
    // this socket has been replaced (stale socket) will be discarded.
    const ws = this.websocket;

    this.websocket.onopen = async () => {
      if (this.websocket !== ws) return;
      this.logger.log('[CALL] assembly - websocket open');
      this.startKeepAlive();

      if (startRecording) {
        await this.connectTranscriber(startRecording);
      }
    };

    this.websocket.onmessage = (event: MessageEvent) => {
      if (this.websocket !== ws) return;
      const turn = JSON.parse(event.data);
      this.logger.log(`[CALL] assembly - ws event: ${JSON.stringify(turn)}`);
      this.trackTurnOrder(turn);

      if (this.isStopRecordingInProgress && !this.pendingFinalResolver) return;

      const currentView = this.callStore.getCurrentView();
      const isPracticeView = currentView === AppConstants.VIEW_PRACTICE;
      const isCallEnded = this.callStore.getDisplayPostCall();

      if (!isPracticeView || isCallEnded) {
        this.stopRecording();
        return;
      }

      if (this.callStore.getIsPaused() && !this.pendingFinalResolver) {
        this.pause();
        return;
      }

      // Drop leftover audio flushed as a new turn after the answer was sent.
      // The turn awaitFinalTranscript() is waiting for is not tagged until the
      // wait ends (see endAwaitFinal), so its partials and final still reach
      // the answer.
      if (this.isAwaitFinalActive && this.isStaleLeftoverTurn(turn)) {
        this.logger.log('[CALL] assembly - discarding stale leftover turn', {
          turn_order: turn.turn_order,
          transcript: turn.transcript,
          end_of_turn: turn.end_of_turn,
        });
        return;
      }

      // ForceEndpoint on a turn with no words yet closes it empty — nothing
      // more is coming, so resolve now instead of running out the timeout.
      if (
        !turn.transcript &&
        turn.end_of_turn === true &&
        this.isAwaitedTurn(turn.turn_order)
      ) {
        this.logger.log(
          '[CALL] assembly - awaited turn closed empty, resolving awaitFinalTranscript',
          { turn_order: turn.turn_order },
        );
        this.pendingFinalResolver!(
          this.callStore.getRecognizedTranscripts().join(' '),
        );
        return;
      }

      if (!turn.transcript) {
        // Universal Streaming v3: an empty-transcript Turn with end_of_turn=false
        // signals speech activity before any words are recognized — treat it as
        // the SpeechStarted equivalent to cancel the turn completion window.
        if (turn.type === 'Turn' && turn.end_of_turn === false) {
          this.logger.log(
            '[CALL] assembly - SpeechStarted received (empty Turn)',
          );
          this._speechStarted$.next();
        }
        return;
      }

      this.handleTranscript(
        turn.transcript,
        turn.end_of_turn,
        turn.turn_is_formatted,
        turn.turn_order,
      );
    };

    this.websocket.onerror = async (error: any) => {
      if (this.websocket !== ws) return;
      this.logger.error('[CALL] assembly - websocket error', error);

      if (this.isStopRecordingInProgress) {
        this.logger.log(
          '[CALL] assembly - websocket error ignored, recording stopped intentionally',
        );
        return;
      }

      await this.resetAndReconnect('websocket error');
    };

    this.websocket.onclose = async (event: CloseEvent) => {
      if (this.websocket !== ws) return;
      this.logger.warn('[CALL] assembly - websocket closed', {
        code: event.code,
        reason: event.reason,
        isStopRecordingInProgress: this.isStopRecordingInProgress,
      });

      if (this.isStopRecordingInProgress) {
        this.logger.log(
          '[CALL] assembly - websocket close ignored, recording stopped intentionally',
        );
        return;
      }

      await this.resetAndReconnect('websocket close');
    };
  }

  async connectTranscriber(startRecording: boolean = true): Promise<void> {
    const readyState = this.websocket?.readyState ?? null;
    this.logger.log('[CALL] assembly - connect transcriber', {
      readyState,
      startRecording,
    });

    if (readyState !== 1) {
      this.logger.error('[CALL] assembly - connect failed, socket not open', {
        readyState,
      });
      if (
        this.controlService &&
        !this.callStore.getDisplayPostCall() &&
        !this.controlService.isEndCallTriggered
      ) {
        this.controlService.clickOpenNoResponse();
      }
      return;
    }

    if (!startRecording) return;

    try {
      const audioContextState = this.audioContext?.state || '';
      const audio = this.audioContext && this.audioWorkletNode;
      this.logger.log(
        `[CALL] assembly - connect transcriber - audioContextState [${audioContextState}]`,
        {
          audioContextState,
          audioContext: this.audioContext,
          audioWorkletNode: this.audioWorkletNode,
        },
      );

      if (audioContextState === 'running' && audio) {
        console.log('AssemblyAI ::: Audio context is running.');
        return;
      }

      if (audioContextState === 'suspended' && audio) {
        console.log('AssemblyAI ::: Audio context is suspended, resuming...');
        await this.resume();
      } else {
        console.log(
          'AssemblyAI ::: Starting recording after connecting to websocket.',
        );
        await this.startRecording((audioData: any) => {
          this.sendAudioData(audioData);
        });
      }
    } catch (error) {
      this.logger.error('[CALL] assembly - connect transcriber error', error);
    }
  }

  async prewarmAudioContext(): Promise<void> {
    if (!this.websocket) {
      this.logger.log('[CALL] assembly - prewarm skipped: no websocket');
      return;
    }
    if (this.audioContext || this.isPrewarmOnProcess || this.isResetting) {
      this.logger.log('[CALL] assembly - prewarm skipped: already warm/in-progress/resetting');
      return;
    }

    this.isPrewarmOnProcess = true;
    const startedAt = performance.now();
    try {
      await this.startRecording((audioData: any) => this.sendAudioData(audioData));
      this.logger.log('[CALL] assembly - prewarm ready', {
        durationMs: Math.round(performance.now() - startedAt),
      });
    } catch (error) {
      this.logger.error('[CALL] assembly - prewarm failed', error, {
        durationMs: Math.round(performance.now() - startedAt),
      });
    } finally {
      this.isPrewarmOnProcess = false;
    }
  }

  sendAudioData(audioData: any): void {
    const isMicOff = !this.callStore.getIsMicOn();
    const isAvatarSpeaking = this.callStore.getIsAvatarSpeaking();
    const isModalOpen = this.callStore.getIsAnyModalOpen();
    if (isMicOff || isAvatarSpeaking || isModalOpen) {
      const reason = isAvatarSpeaking
        ? 'Avatar is speaking, discarding audio data'
        : isModalOpen
          ? 'A modal is open, discarding audio data'
          : 'Mic is off, not sending audio data to AssemblyAI';
      this.logAudioBlocked(`AssemblyAI ::: ${reason}`);
      return;
    }

    if (!this.websocket) {
      this.logAudioBlocked(
        `AssemblyAI ::: websocket is empty on sendAudioData(), retrying...`,
      );
      this.restartTranscriber(false);
      return;
    }

    const state = this.websocket.readyState;
    if (state !== 1) {
      this.logAudioBlocked(
        `AssemblyAI ::: websocket is not open for communication, current state: ${state} on sendAudioData()`,
      );
      // Only restart if fully closed, not if still connecting or closing
      if (state === 3) {
        this.restartTranscriber(false);
      }
      return;
    }
    try {
      this.websocket.send(audioData);
    } catch (error: any) {
      if (error.message.includes('Socket is not open for communication')) {
        this.logAudioBlocked(
          `AssemblyAI ::: websocket is not open for communication on sendAudioData(), retrying...`,
        );
        this.restartTranscriber(false);
        return;
      }
      this.logger.error('[CALL] assembly - Error sending audio data:', error);
    }
  }

  logAudioBlocked(message: string) {
    console.log(message);
    this.logger.log('[CALL] assembly - send audio data blocked', {
      isMicOff: !this.callStore.getIsMicOn(),
      isAvatarSpeaking: this.callStore.getIsAvatarSpeaking(),
      isModalOpen: this.callStore.getIsAnyModalOpen(),
      socketState: this.websocket?.readyState,
      isResetting: this.isResetting,
      message,
    });
  }

  handleTranscript(
    transcript: string,
    end_of_turn: boolean,
    turn_is_formatted: boolean,
    turnOrder?: number,
  ): void {
    let shouldTranscribeCall = this.shouldTranscribe();
    const state = this.audioContext?.state || '';
    const isMicOff = !this.callStore.getIsMicOn();

    if (!this.shouldTranscriberRun) {
      console.log(
        'AssemblyAI ::: shouldTranscriberRun is false, ignoring transcript.',
      );
      return;
    }
    const isAwaitingFinal = !!this.pendingFinalResolver;

    if (state === 'suspended' && !isAwaitingFinal) {
      shouldTranscribeCall = false;
      console.log(
        `AssemblyAI ::: Audio context is suspended, skipping..., ${transcript}`,
      );
      return;
    }

    if (isMicOff && !isAwaitingFinal) {
      console.log(
        `AssemblyAI ::: Mic is off, ignoring transcript: end_of_turn: ${end_of_turn} turn_is_formatted ${turn_is_formatted}:[${transcript}]`,
      );
      return;
    }

    // Assembly_Turn_Settings flag removed — format_turns is permanently off,
    // so only unformatted turns (turn_is_formatted === false) are processed.
    if (!this.isU3ProModel && turn_is_formatted) return;

    // While awaiting, only the awaited turn's final may resolve the wait. If the
    // stale-turn window closed early (e.g. a short avatar echo finished inside
    // the wait), a new turn's final would otherwise be stored into — and
    // resolve — the answer still being built.
    if (
      isAwaitingFinal &&
      end_of_turn &&
      turnOrder !== undefined &&
      this.awaitedTurnOrder !== null &&
      turnOrder !== this.awaitedTurnOrder
    ) {
      this.logger.log(
        '[CALL] assembly - discarding final from non-awaited turn',
        { turnOrder, awaitedTurnOrder: this.awaitedTurnOrder, transcript },
      );
      return;
    }

    if (transcript && (shouldTranscribeCall || isAwaitingFinal)) {
      // The answer this final belongs to was already sent and nothing is waiting
      // for it — storing it would prepend it to the next answer.
      if (end_of_turn && !isAwaitingFinal && this.isSentTurn(turnOrder)) {
        this.logger.warn(
          '[CALL] assembly - dropping late final, answer already sent',
          { turnOrder, transcript },
        );
        this.sentTurnOrder = null;
        return;
      }

      this.callStore.setLastMessageTimeStamp(new Date());
      this.callStore.setUserSpoke(Date.now());
      const isFinal = end_of_turn;
      if (isFinal) {
        console.log('Original Transcript: ' + transcript);
        const correctedTranscript: correctedTranscript = correctTranscript(
          transcript,
          this.callStore.getCallWords(),
          this.callStore.getReplaceWords(),
          this.translocoService.getActiveLang() || 'en',
        );
        this.logger.log('[CALL] assembly - transcript', {
          isFinal,
          end_of_turn,
          turn_is_formatted,
          transcript,
          state: this.websocket?.readyState || 'unknown',
          isMicOn: this.callStore.getIsMicOn(),
          originalText: transcript,
          correctedTranscript: correctedTranscript.transcript,
        });

        this.callStore.setRecognizedTranscripts([
          ...this.callStore.getRecognizedTranscripts(),
          correctedTranscript.transcript,
        ]);

        this.callStore.addTranscriptChangesLogs(correctedTranscript.changes);

        this.callStore.setSpeechTranscript(
          this.callStore.getRecognizedTranscripts().join(' '),
        );
        this.callStore.setPartialTranscriptExists(false);
        this.callStore.setSpeechTranscriptPartial('');

        // Resolve any pending awaitFinalTranscript() call with the complete transcript
        if (this.pendingFinalResolver) {
          this.logger.log(
            '[CALL] assembly - end_of_turn=true received, resolving awaitFinalTranscript',
            { isAwaitingFinal: true },
          );
          this.pendingFinalResolver(
            this.callStore.getRecognizedTranscripts().join(' '),
          );
        }
      } else {
        this.callStore.setPartialTranscriptExists(true);
        this.callStore.setSpeechTranscriptPartial(transcript);
        this.logger.log('[CALL] assembly - transcript partial', {
          isFinal,
          end_of_turn,
          turn_is_formatted,
          transcript,
          state: this.websocket?.readyState || 'unknown',
          isMicOn: this.callStore.getIsMicOn(),
          originalText: transcript,
        });
      }

      // required for manual - prevent unblocking during answer processing
      if (
        this.callStore.getIsContinueButtonBlocked() &&
        !this.callStore.getIsSendingAnswer() &&
        !this.callStore.getIsAvatarSpeaking() &&
        (!this.controlService?.isVisualAidCall ||
          !this.callStore.getIsProcessingAnswer())
      ) {
        console.log(
          `AssemblyAI ::: Unblocking continue button in required for manual check,
          isContinueButtonBlocked: ${this.callStore.getIsContinueButtonBlocked()},
          isSendingAnswer: ${this.callStore.getIsSendingAnswer()},
          isAvatarSpeaking: ${this.callStore.getIsAvatarSpeaking()},
          isProcessingAnswer: ${this.callStore.getIsProcessingAnswer()}`,
        );
        this.callStore.setIsContinueButtonBlocked(false);
      }
    } else {
      console.log(
        `DO NOT SEND! (late response)\n end_of_turn: ${end_of_turn} turn_is_formatted ${turn_is_formatted}:[${transcript}]\n speechTranscript: [${this.callStore.getSpeechTranscript()}]\n speechTranscriptPartial: [${this.callStore.getSpeechTranscriptPartial()}]`,
      );
    }
  }

  shouldTranscribe(): boolean {
    /* NOTE: keep repetitive checks for better understending */
    const isPaused = this.callStore.getIsPaused();
    const isMicOff = !this.callStore.getIsMicOn();
    if (isPaused || isMicOff) {
      return false;
    }

    const manual = AppConstants.ADVANCE_TYPE_MANUAL;
    const isManual = this.callStore.getAdvanceType() == manual;
    const isSendingAnswer = this.callStore.getIsSendingAnswer();
    const isWaitingTimeout = this.callStore.getIsWaitingTimeout();
    const isAvatarSpeaking = this.callStore.getIsAvatarSpeaking();

    // MANUAL CALLS
    if (isManual) {
      if (isSendingAnswer) {
        const isConversational = this.controlService.isConversational();
        if (isConversational) {
          return false;
        }
      }
      if (isWaitingTimeout) {
        return true;
      }
      if (isAvatarSpeaking) {
        return false;
      }
    }

    // AUTOMATIC CALLS
    if (!isManual) {
      if (isSendingAnswer) {
        return false;
      }
    }
    return true;
  }
  // SIMPLIFIED STOP/CLOSE
  // =========================
  async stop(): Promise<void> {
    this.logger.log('[CALL] assembly - stop');

    window.removeEventListener('online', this.onNetworkRestored);
    await this.forceCleanup();

    if (this.controlService?.isVisualAidCall) {
      this.callStore.setIsProcessingAnswer(false);
    }
  }

  async close(): Promise<void> {
    this.logger.log('[CALL] assembly - close');

    await this.forceCleanup();
  }

  getAudioConstraints(): MediaStreamConstraints {
    const device = this.deviceSettingsService?.getAudioDeviceLocalStorage();
    const audioDeviceSaved = device || '';
    return audioDeviceSaved
      ? {
          audio: {
            deviceId: { exact: audioDeviceSaved },
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true },
          },
          video: false,
        }
      : {
          audio: {
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true },
          },
          video: false,
        };
  }

  async startRecording(callback: any): Promise<void> {
    const isCallActive = this.callValidationService.isCallActive();
    this.logger.log('[CALL] assembly - start recording', {
      isCallActive,
      shouldTranscriberRun: this.shouldTranscriberRun,
      isMediaStreamAvailable: !!this.mediaStream,
    });
    if (!isCallActive) {
      console.log(
        `AssemblyAI ::: User is not in an active call — startRecording canceled.`,
      );
      return;
    }
    console.log('start recording: ', this.shouldTranscriberRun);
    if (!this.shouldTranscriberRun) {
      console.log(
        'AssemblyAI ::: Skipping restartTranscriber, call already ended.',
      );
      return;
    }
    if (this.isStartRecordingOnProcess) {
      console.log('AssemblyAI ::: startRecording already in progress, skipping duplicate call.');
      return;
    }
    if (this.audioContext) {
      console.log('AssemblyAI ::: Audio context already exists, skipping duplicate startRecording.');
      return;
    }
    this.isStartRecordingOnProcess = true;
    try {
      await this.buildAudioPipeline(callback);
    } finally {
      this.isStartRecordingOnProcess = false;
    }
  }

  private async buildAudioPipeline(callback: any): Promise<void> {
    console.log(
      `AssemblyAI ::: Starting recording mediaStream [${!!this.mediaStream}]`,
    );
    // validate if the stream is already active
    if (!this.mediaStream) {
      this.mediaStream = await this.requestMicrophoneStream();
      const audioTracks = this.mediaStream.getAudioTracks();
      this.logger.log(
        `[CALL] assembly - Requesting microphone stream on startRecording`,
        { audioTracks },
      );
      if (audioTracks.length > 0) {
        audioTracks[0].addEventListener('ended', () => {
          this.logger.error(`[CALL] assembly -  Audio track disconnected`);
          this.callStore.setIsAudioDeviceSelected(false);
          this.deviceSettingsService.removeAudioDeviceLocalStorage();
          this.controlService.clickOpenNoResponse();
        });
      }
    }
    console.log(`AssemblyAI ::: Creating audio context`);

    // init audio context
    this.audioContext = new AudioContext({
      latencyHint: 'balanced',
      sampleRate: 48000,
    });

    // create a media stream source
    this.mediaStreamSource = this.audioContext.createMediaStreamSource(
      this.mediaStream,
    );

    // add the audio processor to the audio context
    await this.audioContext.audioWorklet.addModule(
      'assets/js/audio-processor.js',
    );

    // create the audio worklet node
    console.log(`AssemblyAI ::: Creating audio worklet node`);
    this.audioWorkletNode = new AudioWorkletNode(
      this.audioContext,
      'audio-processor',
    );

    // connect the media stream source to the audio worklet node
    console.log(`AssemblyAI ::: Connecting audio source to audio worklet node`);
    this.mediaStreamSource.connect(this.audioWorkletNode);
    this.audioWorkletNode.connect(this.audioContext.destination);

    // listen for audio data from the audio worklet node
    console.log(`AssemblyAI ::: Listening for audio data`);
    this.audioWorkletNode.port.onmessage = async (event) => {
      if (event.data.error) {
        this.logger.error(
          '[CALL] assembly - workletnode error received from audio processor',
          { error: event.data.error },
        );
        this.isErrorOnAudioProcessor = true;
        this.validateWebAudioStatus();
        return;
      }
      this.isErrorOnAudioProcessor = false;
      this.processAudioData(event, callback);
    };

    this.audioContext.addEventListener('statechange', () => {
      console.log(
        `AssemblyAI ::: Audio context state changed to ${this.audioContext?.state}`,
      );
    });
  }

  processAudioData(event: any, callback: any): void {
    // Check if mic is off - don't process audio data if mic is off
    const isMicOff = !this.callStore.getIsMicOn();
    if (isMicOff) {
      // console.log(`AssemblyAI ::: Mic is off, not processing audio data`);
      return;
    }

    const currentBuffer = new Int16Array(event.data.audio_data);
    // merge the audio buffers together to create a single buffer of audio data
    this.audioBufferQueue = this.mergeBuffers(
      this.audioBufferQueue,
      currentBuffer,
    );

    if (this.audioContext) {
      const sampleRate = 16000; // audio-processor.js downsamples to 16 kHz
      const abQueueLength = this.audioBufferQueue.length;
      const bufferDuration = (abQueueLength / sampleRate) * 1000; // in ms

      // wait until we have 100ms of audio data
      if (bufferDuration >= 100) {
        const totalSamples = Math.floor(sampleRate * 0.1); // 1 600 = 100ms @ 16 kHz
        const now = performance.now();
        if (now - this._lastChunkLogAt >= 1000) {
          this.logger.log('[CALL] assembly - audio chunk buffered', {
            samples: totalSamples,
            chunkMs: Math.round((totalSamples / sampleRate) * 1000),
          });
          this._lastChunkLogAt = now;
        }

        // get the first 100ms of audio data
        const chunkSlice = this.audioBufferQueue.subarray(0, totalSamples);
        this.emitAudioLevel(chunkSlice);
        const finalBuffer = new Uint8Array(chunkSlice.buffer);

        // remove the first 100ms of audio data
        this.audioBufferQueue = this.audioBufferQueue.subarray(totalSamples);

        // send the audio data to the callback
        if (callback) callback(finalBuffer);
      }
    }
  }

 
  private emitAudioLevel(chunk: Int16Array): void {
    // RMS = Root Mean Square
    // It's a standard way to measure the average power/loudness of an audio signal. The formula:
    // RMS = √( (s₁² + s₂² + s₃² + ... + sₙ²) / n )

    let sumOfSquares = 0;
    for (let i = 0; i < chunk.length; i++) {
      sumOfSquares += chunk[i] * chunk[i];
    }
    const RMS = Math.sqrt(sumOfSquares / chunk.length);
    this._audioLevel$.next(RMS);
  }

  mergeBuffers(left: Int16Array, right: Int16Array): Int16Array {
    const result = new Int16Array(left.length + right.length);
    result.set(left, 0);
    result.set(right, left.length);
    return result;
  }

  async stopRecording(): Promise<void> {
    this.logger.log('[CALL] assembly - stop recording');

    this.isStopRecordingInProgress = true;
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }
    await this.stopAudioAPI();
  }

  async stopAudioAPI(): Promise<void> {
    this.logger.log('[CALL] assembly - stop audio API');
    this.audioBufferQueue = new Int16Array(0);
    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }
    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }
    if (this.audioContext) {
      const state = this.audioContext?.state || '';
      if (state === 'closed') {
        this.logger.log(`[CALL] assembly - Audio context closed already`);
        return;
      }
      if (this.isStopAudioAPIOnProcess) {
        return;
      }
      this.isStopAudioAPIOnProcess = true;
      await this.audioContext.close();
      this.logger.log('[CALL] assembly - audioContext closed');
      this.isStopAudioAPIOnProcess = false;
      this.audioContext = null;
    }
    this.audioBufferQueue = new Int16Array(0);
    this.isResumeOnProcess = false;
  }

  async getSttConfig(): Promise<ISttConnectionConfig> {
    try {
      const callUuid = this.callStore.getCallUuid();
      const response = await firstValueFrom(
        this.callService
          .getSttConnectionConfig(callUuid)
          .pipe(retry({ count: 5, delay: 1000 })),
      );
      this.logger.log('[CALL] assembly - get stt config', { response });
      return response;
    } catch (error: Error | any) {
      this.logger.error(
        '[CALL] assembly - get stt config failed after retries',
        error,
        { error: error.message },
      );
      throw error;
    }
  }

  async pause(): Promise<boolean> {
    this.logger.log('[CALL] assembly - audio API pause', {
      hasAudioContext: !!this.audioContext,
      state: this.audioContext?.state || 'none',
      isPauseOnProcess: this.isPauseOnProcess,
    });
    if (!this.audioContext) {
      this.logger.warn('[CALL] assembly - pause skipped: audioContext is null');
      return false;
    }
    if (this.isPauseOnProcess) {
      this.logger.warn(
        '[CALL] assembly - pause skipped: isPauseOnProcess is true',
      );
      return false;
    }
    const state = this.audioContext.state;
    if (state === 'suspended' || state === 'closed') {
      this.logger.warn(
        `[CALL] assembly - pause skipped: audioContext already ${state}`,
      );
      return false;
    }
    this.audioBufferQueue = new Int16Array(0);
    this.isPauseOnProcess = true;
    await this.audioContext.suspend();
    this.audioBufferQueue = new Int16Array(0);
    console.log(`AssemblyAI ::: Audio context suspended`);
    this.isPauseOnProcess = false;
    return true;
  }

  async resume(): Promise<boolean> {
    const state = this.audioContext?.state || 'none';
    this.logger.log('[CALL] assembly - audio API resume', {
      hasAudioContext: !!this.audioContext,
      hasAudioWorkletNode: !!this.audioWorkletNode,
      isResumeOnProcess: this.isResumeOnProcess,
      state,
    });
    if (!this.audioContext) {
      this.logger.warn(
        '[CALL] assembly - resume skipped: audioContext is null — transcriber was destroyed and not yet rebuilt',
      );
      return false;
    }
    if (!this.audioWorkletNode) {
      this.logger.warn(
        '[CALL] assembly - resume skipped: audioWorkletNode is null',
      );
      return false;
    }
    if (this.isResumeOnProcess) {
      this.logger.warn(
        '[CALL] assembly - resume skipped: isResumeOnProcess is true',
      );
      return false;
    }
    if (state === 'running') {
      this.logger.log(
        '[CALL] assembly - resume skipped: audioContext already running',
      );
      return false;
    }
    this.isResumeOnProcess = true;
    this.audioBufferQueue = new Int16Array(0);
    try {
      await this.audioContext.resume();
      this.logger.log('[CALL] assembly - audio context resumed successfully');
      console.log(`AssemblyAI ::: Audio context resumed`);
    } catch (error) {
      this.logger.error('[CALL] assembly - resume failed', error);
      console.warn(`AssemblyAI ::: Error resuming audio context: ${error}`);
    }
    if (!this.websocket) {
      this.logger.warn(
        '[CALL] assembly - websocket missing after resume — restarting transcriber',
      );
      await this.startTranscriber(false);
    }
    this.isResumeOnProcess = false;
    return true;
  }

  getAudioContextState(): string {
    return this.audioContext?.state || '';
  }

  forceEndUtterance(): void {
    this.logger.log('[CALL] assembly - force end statement');
    if (this.websocket && this.websocket.readyState === 1) {
      this.websocket.send(JSON.stringify({ type: 'ForceEndpoint' }));
      this.audioBufferQueue = new Int16Array(0);
      console.log(`AssemblyAI ::: Force end utterance`);
    }
  }

  awaitFinalTranscript(): Promise<string> {
    // The turn whose final belongs to this answer: the open turn, or — if the
    // last turn already closed — the next one, where speech still in flight
    // will arrive. Only this turn's final may pass the stale-turn filter.
    this.awaitedTurnOrder = this.getOpenTurnOrder();

    // Open the leftover-turn window: from here until the next question resumes
    // the mic, any newly-born turn is buffered/echo audio, not a fresh response.
    this.beginStaleTurnWindow();

    // With no open partial the turn may have closed naturally (AssemblyAI's own
    // min_turn_silence/max_turn_silence fired before Continue was pressed), and
    // ForceEndpoint on an already-closed turn gets no response at all. But
    // speech may still be in flight (U3.5 Pro emits partials only every 1.5–2s),
    // so still force a final — just wait for it with a shorter timeout.
    const hasOpenPartial = !!this.callStore.getSpeechTranscriptPartial();
    const timeoutMs = hasOpenPartial
      ? this.awaitFinalTimeoutMs
      : this.AWAIT_FINAL_SKIP_TIMEOUT_MS;

    // Cancel any previous pending resolver before starting a new one
    this.pendingFinalResolver = null;

    this.logger.log(
      '[CALL] assembly - awaitFinalTranscript started, sending ForceEndpoint and waiting for end_of_turn=true',
      { hasOpenPartial, awaitedTurnOrder: this.awaitedTurnOrder, timeoutMs },
    );
    this.forceEndUtterance();

    return new Promise<string>((resolve) => {
      const timeout = setTimeout(() => {
        this.endAwaitFinal();
        // Fallback: use recognized transcripts (already corrected).
        // If empty, return empty string — processUserResponse handles the uncorrected partial as last resort.
        const fallback = this.callStore.getRecognizedTranscripts().join(' ');
        this.logger.log(
          `[CALL] assembly - awaitFinalTranscript ${timeoutMs}ms timeout reached, using fallback`,
          { fallback },
        );
        resolve(fallback);
      }, timeoutMs);

      this.pendingFinalResolver = (transcript: string) => {
        clearTimeout(timeout);
        this.endAwaitFinal();
        this.logger.log(
          '[CALL] assembly - awaitFinalTranscript resolved via end_of_turn=true',
          { transcript },
        );
        resolve(transcript);
      };
    });
  }

  updateValidationState(): void {
    this.callStore.setIsErrorOnAudioValidation(false);
    this.callStore.setIsLoadingNoResponse(false);
    this.callStore.setOpenNoResponseModal(false);
  }

  getCurrentTranscription(): string {
    const final = this.final;
    const partial = this.partial;
    if (partial.length > 0) this.lastPartialUsed = partial;
    this.final = '';
    this.partial = '';
    console.log(final + ':::::::::::' + partial);

    return final + ' ' + partial;
  }
  async requestMicrophoneStream(): Promise<MediaStream> {
    const constraints = this.getAudioConstraints();
    this.logger.log('[CALL] assembly - request microphone stream', {
      constraints,
    });
    return await navigator.mediaDevices.getUserMedia(constraints);
  }

  // =========================
  // OPTIONAL LIGHT VALIDATION
  // =========================
  async validateWebAudioStatus(): Promise<void> {
    this.logger.log('[CALL] assembly - validate web audio');

    if (!navigator.onLine) {
      this.callStore.setIsLoadingNoResponse(true);

      setTimeout(() => {
        this.callStore.setIsLoadingNoResponse(false);
      }, 350);

      return;
    }

    if (!this.websocket || this.websocket.readyState !== 1) {
      await this.resetAndReconnect('validation failed');
      return;
    }

    this.updateValidationState();
  }

  // =========================
  // KEEP-ALIVE
  // =========================
  private startKeepAlive(): void {
    this.stopKeepAlive();
    this.keepAliveInterval = setInterval(() => {
      if (this.websocket?.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({ type: 'KeepAlive' }));
        this.logger.log('[CALL] assembly - keepalive sent');
      }
    }, this.KEEPALIVE_INTERVAL_MS);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveInterval !== null) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  // =========================
  // NEW CENTRALIZED CLEANUP
  // =========================
  private async forceCleanup(): Promise<void> {
    this.logger.log('[CALL] assembly - force cleanup');

    this.stopKeepAlive();
    this.isStopRecordingInProgress = true;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.websocket) {
      try {
        this.websocket.onopen = null;
        this.websocket.onmessage = null;
        this.websocket.onerror = null;
        this.websocket.onclose = null;

        if (this.websocket.readyState <= 1) {
          this.websocket.close();
        }
      } catch (err) {
        this.logger.error('[CALL] assembly - websocket cleanup error', err);
      }

      this.websocket = null;
    }

    await this.stopAudioAPI();

    this.audioBufferQueue = new Int16Array(0);

    // A fresh session restarts turn_order at 0, so any tags from the old
    // session would collide with new turns — clear them.
    this.resetStaleTurnTracking();
  }

  // =========================
  // NEW CENTRALIZED RESET
  // =========================
  private async resetAndReconnect(
    reason: string,
    startRecording: boolean = true,
  ): Promise<void> {
    if (this.isResetting) {
      this.logger.log('[CALL] assembly - reset already in progress', {
        reason,
      });
      return;
    }

    const now = Date.now();
    if (now - this.lastReconnectAt < this.RECONNECT_COOLDOWN_MS) {
      this.logger.log('[CALL] assembly - reconnect throttled by cooldown', {
        reason,
        msSinceLastReset: now - this.lastReconnectAt,
      });
      return;
    }

    this.lastReconnectAt = now;
    this.isResetting = true;
    this.callStore.setIsReconnectingAudio(true);

    this.logger.warn('[CALL] assembly - reset and reconnect', {
      reason,
      startRecording,
    });

    try {
      await this.forceCleanup();

      this.sttConfig = await this.getSttConfig();
      this.assemblyAIToken = this.sttConfig.token;
      this.applySttUrlSettings(this.sttConfig.url);

      await this.startTranscriber(startRecording);
    } catch (error) {
      this.logger.error('[CALL] assembly - reset failed', error);
    } finally {
      this.isResetting = false;
      this.callStore.setIsReconnectingAudio(false);
    }
  }

  // NOT reset: mediaStream, audioContext, audioWorkletNode, mediaStreamSource,
  //   audioBufferQueue, websocket — all require proper async cleanup via stop()/forceCleanup()
  // NOT reset: shouldTranscriberRun — never set to false; always true by design
  // NOT reset: isInternetConnected — reflects live network state, not call-scoped
  // NOT reset: assemblyTurnSettingsEnabled — feature flag, refreshed in init()
  // NOT reset: keepAliveInterval — cleaned up by stopKeepAlive() inside forceCleanup()
  // NOT reset: pendingFinalResolver — forcing it to null here would leave a caller
  //   awaiting awaitFinalTranscript() hanging forever. It already self-clears via its
  //   own internal timeout.
  resetForNewCall(): void {
    this.isPauseOnProcess = false;
    this.isStopRecordingInProgress = false;
    this.isErrorOnAudioProcessor = false;
    this.isStopAudioAPIOnProcess = false;
    this.isResumeOnProcess = false;
    this.partial = '';
    this.final = '';
    this.lastPartialUsed = '';
    this.assemblyAIToken = null;
    this.isResetting = false;
    this.lastReconnectAt = 0;
  }

  // A new session has no memory of any turn from the previous one. If a
  // partial was still open (never finalized) when the previous session ended
  // (e.g. user muted mid-sentence before a mic-toggle restart), preserve it as
  // best-effort recognized text before starting fresh — otherwise it would be
  // silently lost, and the stale partial would incorrectly make
  // awaitFinalTranscript() think a turn is still open on this new session.
  private preserveStalePartialBeforeRestart(): void {
    const stalePartial = this.callStore.getSpeechTranscriptPartial();
    if (!stalePartial) return;

    const alreadyIncluded = this.callStore
      .getRecognizedTranscripts()
      .includes(stalePartial);
    if (!alreadyIncluded) {
      this.callStore.setRecognizedTranscripts([
        ...this.callStore.getRecognizedTranscripts(),
        stalePartial,
      ]);
      this.logger.log(
        '[CALL] assembly - preserved stale partial as recognized text before session restart',
        { stalePartial },
      );
    }
    this.callStore.setSpeechTranscriptPartial('');
    this.callStore.setPartialTranscriptExists(false);
  }

  // Open the post-answer window. Any turn_order observed while this is active
  // — whether it's brand new or one that started before the window opened —
  // gets tagged as leftover and its events are dropped in onmessage. Called
  // from awaitFinalTranscript(), so it only runs when isAwaitFinalActive.
  private beginStaleTurnWindow(): void {
    this.discardStaleTurnWindowActive = true;
  }

  // Close the window once the next question has finished and the mic resumes.
  // Turns tagged stale during the window stay dropped (their trailing/final
  // events keep getting suppressed even after this closes); turns never
  // touched during the window (genuine responses) are handled normally.
  endStaleTurnWindow(): void {
    this.discardStaleTurnWindowActive = false;
  }

  private isStaleLeftoverTurn(turn: any): boolean {
    const order = turn?.turn_order;
    if (typeof order !== 'number') return false;

    // Tag on every sighting while the window is active — not just the first —
    // so a turn_order that AssemblyAI opened before Continue was pressed (from
    // its own silence detection) still gets caught the moment it emits an
    // event inside the window. Once tagged, it stays dropped for the session.
    // The awaited turn is the exception while the wait is on — it is tagged
    // by endAwaitFinal() once it has actually ended.
    if (this.discardStaleTurnWindowActive && !this.isAwaitedTurn(order)) {
      this.staleTurnOrders.add(order);
    }
    return this.staleTurnOrders.has(order);
  }

  private resetStaleTurnTracking(): void {
    this.discardStaleTurnWindowActive = false;
    this.staleTurnOrders.clear();
    this.lastTurnOrder = null;
    this.lastTurnClosed = true;
    this.awaitedTurnOrder = null;
    this.sentTurnOrder = null;
  }

  // Called by the caller right after it empties the transcript store to send the
  // answer. If a turn was still open at that moment, its final will arrive late
  // and belongs to the answer just sent — remember it so handleTranscript drops it.
  markAnswerSent(): void {
    this.sentTurnOrder =
      this.lastTurnOrder !== null && !this.lastTurnClosed
        ? this.lastTurnOrder
        : null;
  }

  private trackTurnOrder(turn: any): void {
    const order = turn?.turn_order;
    if (typeof order !== 'number') return;
    if (this.lastTurnOrder !== null && order < this.lastTurnOrder) return;
    this.lastTurnOrder = order;
    this.lastTurnClosed = turn.end_of_turn === true;
  }

  private getOpenTurnOrder(): number {
    if (this.lastTurnOrder === null) return 0;
    return this.lastTurnClosed ? this.lastTurnOrder + 1 : this.lastTurnOrder;
  }

  private isAwaitedTurn(order: number | undefined): boolean {
    return (
      !!this.pendingFinalResolver &&
      order !== undefined &&
      order === this.awaitedTurnOrder
    );
  }

  // Ends the wait (final received or timeout). The awaited turn has now really
  // ended, so tag it as leftover: its late final (after a timeout) or trailing
  // events must not reach the next answer. Only tag it if it was actually seen —
  // with no open partial the awaited turn is the *next* turn_order, and if it
  // never appeared that number belongs to the rep's genuine next answer.
  private endAwaitFinal(): void {
    const awaited = this.awaitedTurnOrder;
    if (
      awaited !== null &&
      this.lastTurnOrder !== null &&
      awaited <= this.lastTurnOrder
    ) {
      this.staleTurnOrders.add(awaited);
    }
    this.awaitedTurnOrder = null;
    this.pendingFinalResolver = null;
  }

  private isSentTurn(turnOrder: number | undefined): boolean {
    return turnOrder !== undefined && turnOrder === this.sentTurnOrder;
  }
}